import type { ControlState } from "../../sim";

export type Action = keyof ControlState;
export const ACTIONS: readonly Action[] = ["thrust", "left", "right", "fireLaser", "fireMissile"];

/** Keyboard bindings for one player: action → list of `KeyboardEvent.code` values. */
export type KeyBindings = Record<Action, string[]>;

/**
 * Original 2008 layout (keys.cfg + Controls.JPG), one shared keyboard:
 * player 1 (blue) on the arrow keys, player 2 (red) on WASD.
 */
export const DEFAULT_BINDINGS: readonly KeyBindings[] = [
	{
		thrust: ["ArrowUp"],
		left: ["ArrowLeft"],
		right: ["ArrowRight"],
		fireLaser: ["Space"],
		fireMissile: ["ControlRight"],
	},
	{
		thrust: ["KeyW"],
		left: ["KeyA"],
		right: ["KeyD"],
		fireLaser: ["Tab"],
		fireMissile: ["ShiftLeft"],
	},
	// Players 3 and 4 (P11.3): the IJKL block and the numeric keypad. Gamepads 3 and 4 work too.
	{
		thrust: ["KeyI"],
		left: ["KeyJ"],
		right: ["KeyL"],
		fireLaser: ["KeyK"],
		fireMissile: ["KeyU"],
	},
	{
		thrust: ["Numpad8"],
		left: ["Numpad4"],
		right: ["Numpad6"],
		fireLaser: ["Numpad5"],
		fireMissile: ["Numpad0"],
	},
];

/** Derives a player's controls from the set of currently held key codes. */
export function controlsFromKeys(held: ReadonlySet<string>, bindings: KeyBindings): ControlState {
	const any = (codes: string[]) => codes.some((code) => held.has(code));
	return {
		thrust: any(bindings.thrust),
		left: any(bindings.left),
		right: any(bindings.right),
		fireLaser: any(bindings.fireLaser),
		fireMissile: any(bindings.fireMissile),
	};
}

/** Every key code used by the given bindings (these get `preventDefault` during play). */
export function boundCodes(bindings: readonly KeyBindings[]): Set<string> {
	return new Set(bindings.flatMap((b) => ACTIONS.flatMap((a) => b[a])));
}

/** ORs several input sources for the same player (e.g. keyboard + gamepad). */
export function mergeControls(...sources: ControlState[]): ControlState {
	return {
		thrust: sources.some((s) => s.thrust),
		left: sources.some((s) => s.left),
		right: sources.some((s) => s.right),
		fireLaser: sources.some((s) => s.fireLaser),
		fireMissile: sources.some((s) => s.fireMissile),
	};
}

/** Keys that can't be bound to ship controls (menus, pause, fullscreen, mute). */
export const RESERVED_CODES: ReadonlySet<string> = new Set(["Escape", "KeyF", "KeyM"]);

/**
 * Binds `code` to one player's action. If the key is already used by any player's action,
 * that action receives this action's previous key instead (a swap), so nothing is left
 * silently unbound. Returns new bindings; the input is not modified.
 */
export function rebind(
	bindings: readonly KeyBindings[],
	player: number,
	action: Action,
	code: string,
): KeyBindings[] {
	const next = bindings.map((b) => ({
		thrust: [...b.thrust],
		left: [...b.left],
		right: [...b.right],
		fireLaser: [...b.fireLaser],
		fireMissile: [...b.fireMissile],
	}));
	const target = next[player];
	if (!target) return next;
	const previous = target[action];

	next.forEach((b, p) => {
		for (const a of ACTIONS) {
			if ((p !== player || a !== action) && b[a].includes(code)) {
				b[a] = [...b[a].filter((c) => c !== code), ...previous.filter((c) => !b[a].includes(c))];
			}
		}
	});
	target[action] = [code];
	return next;
}
